import { uniqueUploadName, getUploadMimeType } from "~/utils/image-upload";
import {
  getDownloadURL as getDownloadURLFirebase,
  getStorage,
  ref as storageRef,
  StorageReference,
  uploadBytesResumable,
  UploadTask,
} from "@firebase/storage";
import { useFirebaseStorage } from "vuefire";
import { SupportedFileMimeType } from "~/types/enums/SupportedFileMimeType.enum";

export const useFileUploader = () => {
  const storage = useFirebaseStorage();
  const altStorage = getStorage();

  const createMediaObject = async (
    file: File | CustomFile,
    path?: string,
    includeTimestamp: boolean = true
  ): Promise<MediaObject> => {
    const uid = useCurrentUID();

    if (!path) {
      path = `/user-uploads/${uid}`;
    }

    // Sanitize file name to prevent unsafe characters (e.g., slashes, backslashes, etc.)
    const fileName = file.name.replace(/[\\/:*?"<>|]/g, "_");

    if (includeTimestamp != true) {
      path += `/${fileName}`;
    } else {
      path += `/${uniqueUploadName(fileName)}`;
    }

    const fileRef = storageRef(storage, path);

    return {
      id: crypto.randomUUID(),
      name: fileRef.name,
      originalFileName: file.name,
      fullPath: fileRef.fullPath,
      size: file.size,
      bucket: fileRef.bucket,
      contentType: getUploadMimeType(file),
      mediaType: getUploadMimeType(file),
      mediaHref: "",
      mimeType: getUploadMimeType(file) as SupportedFileMimeType,
    };
  };

  const uploadFile = (
    file: File | Uint8Array | ArrayBuffer | Blob,
    mediaObject: MediaObject
  ): UploadTask => {
    const fileRef = storageRef(storage, mediaObject.fullPath);
    return uploadBytesResumable(fileRef, file, { contentType: mediaObject.contentType });
  };

  const uploadFileFromBuffer = (
    data: ArrayBuffer,
    mediaObject: MediaObject
  ): UploadTask => {
    const fileRef = storageRef(storage, mediaObject.fullPath);

    return uploadBytesResumable(fileRef, data, { contentType: mediaObject.contentType });
  };

  const uploadFileAndCreateMediaObject = async (
    file: File,
    path?: string
  ): Promise<UploadTask> => {
    const mediaObject = await createMediaObject(file, path);
    const uploadTask = uploadFile(file, mediaObject);
    return uploadTask;
  };

  const uploadFileFromBufferAndCreateMediaObject = async (
    file: CustomFile,
    path?: string
  ): Promise<UploadTask> => {
    const mediaObject = await createMediaObject(file, path);
    const uploadTask = uploadFileFromBuffer(file.data as Buffer, mediaObject);
    return uploadTask;
  };

  const getCleanFileName = (fileName: string): string => {
    const firstExpression = /\[(.*[0-9])\]_/;
    const name = fileName.replace(firstExpression, "").replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, "");
    const lastIndex = name.lastIndexOf(".");

    return name.substring(0, lastIndex);
  };

  const getDownloadURL = (fileRef: StorageReference): Promise<string> => {
    return getDownloadURLFirebase(fileRef);
  };

  return {
    createMediaObject,
    uploadFile,
    uploadFileFromBuffer,
    uploadFileAndCreateMediaObject,
    uploadFileFromBufferAndCreateMediaObject,
    getDownloadURL,
    getCleanFileName,
  };
};
